import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  CardMedia,
  Chip,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  EmojiEvents, 
  Business, 
  Groups, 
  Tour, 
  CheckCircle, 
  Email, 
  Phone, 
  LinkedIn, 
  Twitter, 
  Instagram, 
  Facebook,
  PersonAdd 
} from '@mui/icons-material';
import { aboutPageService } from '../services/firebase';
import WhatsAppButton from '../components/Common/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const [aboutData, setAboutData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError(null);
      let data = await aboutPageService.getAboutPageData();
      
      // Eğer veri yoksa varsayılan veriyi oluştur
      if (!data) {
        await aboutPageService.initializeAboutPageData();
        data = await aboutPageService.getAboutPageData();
      }
      
      setAboutData(data);
    } catch (err) {
      console.error('Hakkımızda verileri yüklenirken hata:', err);
      setError('Sayfa verileri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'linkedin': return <LinkedIn />;
      case 'twitter': return <Twitter />;
      case 'instagram': return <Instagram />;
      case 'facebook': return <Facebook />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !aboutData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          {error || 'Sayfa verileri yüklenemedi'}
        </Typography>
      </Box>
    );
  }

  const {
    heroSection,
    statistics,
    missionVision,
    teamMembers,
    companyHistory,
    certificates,
    sections,
  } = aboutData || {};

  const safeSections = sections || {
    statistics: { isActive: true },
    missionVision: { isActive: true },
    values: { isActive: true },
    team: { isActive: true },
    history: { isActive: true },
    certificates: { isActive: true },
  };

  const stats = statistics ? [
    { label: t('about_us.years_experience'), value: statistics.yearsExperience, icon: <Business /> },
    { label: t('about_us.happy_customers'), value: statistics.happyCustomers, icon: <Groups /> },
    { label: t('about_us.tours_completed'), value: statistics.toursCompleted, icon: <Tour /> },
    { label: t('about_us.destinations'), value: statistics.destinations, icon: <CheckCircle /> },
  ] : [];

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(heroSection?.title) || t('about_us.title')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(heroSection?.description) || `Hakkımızda sayfamız.`} />
      </Helmet>
      
      <Box sx={{ backgroundColor: 'var(--light-gray-white)', minHeight: '100vh' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' }, 
                background: 'linear-gradient(45deg, var(--navy-blue), var(--light-blue-cta))',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3 
              }}
            >
              {getTranslatedData(heroSection?.title) || 'Biz Kimiz?'}
            </Typography>
            <Typography 
              variant="h5" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                fontWeight: 300, 
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', md: '1.3rem' }
              }}
            >
              {getTranslatedData(heroSection?.description) || ''}
            </Typography>
          </Box>

          {/* Statistics */}
          {safeSections?.statistics?.isActive && stats.length > 0 && (
            <Card sx={{ 
              mb: 8, 
              p: { xs: 3, md: 6 }, 
              backgroundColor: 'white', 
              borderRadius: 4, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
              }
            }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, 
                gap: 4 
              }}>
                {stats.map((stat, index) => (
                  <Box key={stat.label} sx={{ 
                    textAlign: 'center',
                    position: 'relative',
                    '&::after': index < stats.length - 1 ? {
                      content: '""',
                      position: 'absolute',
                      right: { xs: index % 2 === 0 ? -16 : 'auto', md: -16 },
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '1px',
                      height: '60%',
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      display: { xs: index % 2 === 0 ? 'block' : 'none', md: 'block' }
                    } : {}
                  }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'var(--navy-blue)', 
                        mb: 1,
                        fontSize: { xs: '1.8rem', md: '2.5rem' }
                      }}
                    >
                      {stat.value.toLocaleString('tr-TR')}+
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 400,
                        fontSize: { xs: '0.9rem', md: '1.1rem' }
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          )}

          {/* Mission & Vision */}
          {safeSections?.missionVision?.isActive && missionVision && (
            <Box sx={{ mb: 8, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
              <Card sx={{ 
                backgroundColor: 'white', 
                borderRadius: 4, 
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-2px)' }
              }}>
                <CardContent sx={{ p: 5 }}>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'var(--light-blue-cta)', mb: 3 }}>
                    {t('about_us.our_mission')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 400, lineHeight: 1.8, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                    {getTranslatedData(missionVision.mission)}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ 
                backgroundColor: 'white', 
                borderRadius: 4, 
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
                transition: 'transform 0.3s ease',
                '&:hover': { transform: 'translateY(-2px)' }
              }}>
                <CardContent sx={{ p: 5 }}>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'var(--light-blue-cta)', mb: 3 }}>
                    {t('about_us.our_vision')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 400, lineHeight: 1.8, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                    {getTranslatedData(missionVision.vision)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Values */}
          {safeSections?.values?.isActive && missionVision?.values && missionVision.values.length > 0 && (
            <Box sx={{ mb: 8 }}>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700, 
                  textAlign: 'center', 
                  mb: 6, 
                  color: 'var(--navy-blue)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 4,
                    backgroundColor: 'var(--light-blue-cta)',
                    borderRadius: 2
                  }
                }}
              >
                {t('about_us.our_values')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {missionVision.values.map((value: string, index: number) => (
                  <Chip 
                    key={value} 
                    label={value} 
                    sx={{ 
                      fontSize: '1.1rem', 
                      padding: '20px 15px', 
                      fontWeight: 500, 
                      backgroundColor: 'white', 
                      color: 'var(--navy-blue)', 
                      border: '2px solid var(--light-blue-cta)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'var(--light-blue-cta)',
                        color: 'white',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                      }
                    }} 
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Team Members */}
          {safeSections?.team?.isActive && (
          <Box sx={{ mb: 8 }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                textAlign: 'center', 
                mb: 6, 
                color: 'var(--navy-blue)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 60,
                  height: 4,
                  backgroundColor: 'var(--light-blue-cta)',
                  borderRadius: 2
                }
              }}
            >
              {t('about_us.our_team')}
            </Typography>
            
            {teamMembers && teamMembers.length > 0 ? (
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 4,
                justifyItems: 'center'
              }}>
                {teamMembers
                  .filter((member: any) => member.isActive)
                  .sort((a: any, b: any) => a.order - b.order)
                  .map((member: any) => (
                    <Card 
                      key={member.id} 
                      sx={{ 
                        textAlign: 'center', 
                        p: 4, 
                        width: '100%',
                        maxWidth: 320,
                        backgroundColor: 'white', 
                        borderRadius: 4, 
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 16px 40px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      <Avatar 
                        alt={member.name} 
                        src={member.image || '/default-avatar.png'} 
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          margin: '0 auto 16px', 
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          border: '4px solid var(--light-blue-cta)'
                        }} 
                      />
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        sx={{ 
                          fontWeight: 600, 
                          color: 'var(--navy-blue)', 
                          mb: 1 
                        }}
                      >
                        {getTranslatedData(member.name)}
                      </Typography>
                      <Typography 
                        color="text.secondary" 
                        gutterBottom 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 500,
                          color: 'var(--light-blue-cta)'
                        }}
                      >
                        {getTranslatedData(member.position)}
                      </Typography>
                      {member.department && (
                        <Chip 
                          label={member.department} 
                          size="small" 
                          sx={{ 
                            mb: 2, 
                            backgroundColor: 'var(--light-gray)', 
                            color: 'var(--navy-blue)' 
                          }} 
                        />
                      )}
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mb: 3, lineHeight: 1.6 }}
                      >
                        {getTranslatedData(member.bio)}
                      </Typography>
                      
                      {/* Contact & Social Media */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 1, 
                        flexWrap: 'wrap' 
                      }}>
                        {member.email && (
                          <Tooltip title={member.email}>
                            <IconButton 
                              size="small" 
                              href={`mailto:${member.email}`}
                              sx={{ 
                                color: 'var(--navy-blue)',
                                '&:hover': { color: 'var(--light-blue-cta)' }
                              }}
                            >
                              <Email />
                            </IconButton>
                          </Tooltip>
                        )}
                        {member.phone && (
                          <Tooltip title={member.phone}>
                            <IconButton 
                              size="small" 
                              href={`tel:${member.phone}`}
                              sx={{ 
                                color: 'var(--navy-blue)',
                                '&:hover': { color: 'var(--light-blue-cta)' }
                              }}
                            >
                              <Phone />
                            </IconButton>
                          </Tooltip>
                        )}
                        {member.socialMedia && Object.entries(member.socialMedia).map(([platform, url]) => (
                          url && (
                            <Tooltip key={platform} title={`${platform} profilini görüntüle`}>
                              <IconButton 
                                size="small" 
                                href={url as string} 
                                target="_blank"
                                sx={{ 
                                  color: 'var(--navy-blue)',
                                  '&:hover': { color: 'var(--light-blue-cta)' }
                                }}
                              >
                                {getSocialIcon(platform)}
                              </IconButton>
                            </Tooltip>
                          )
                        ))}
                      </Box>
                    </Card>
                  ))}
              </Box>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                backgroundColor: 'white',
                borderRadius: 4,
                boxShadow: '0 4px 16px rgba(0,0,0,0.05)'
              }}>
                <PersonAdd sx={{ fontSize: 64, color: 'var(--light-blue-cta)', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ color: 'var(--navy-blue)' }}>
                  Ekip üyeleri yakında eklenecek
                </Typography>
                <Typography color="text.secondary">
                  Harika ekibimizi tanıtmak için hazırlık yapıyoruz.
                </Typography>
              </Box>
            )}
          </Box>
          )}

          {/* History */}
          {safeSections?.history?.isActive && companyHistory?.milestones && companyHistory.milestones.length > 0 && (
            <Box sx={{ mb: 8 }}>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700, 
                  textAlign: 'center', 
                  mb: 6, 
                  color: 'var(--navy-blue)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 4,
                    backgroundColor: 'var(--light-blue-cta)',
                    borderRadius: 2
                  }
                }}
              >
                {t('about_us.our_history')}
              </Typography>
              <Paper elevation={0} sx={{ 
                p: { xs: 2, md: 4 }, 
                backgroundColor: 'white', 
                borderRadius: 4, 
                boxShadow: '0 8px 24px rgba(0,0,0,0.05)' 
              }}>
                <List>
                  {companyHistory.milestones
                    .filter((milestone: any) => milestone.isActive)
                    .sort((a: any, b: any) => a.year - b.year)
                    .map((milestone: any, index: number) => (
                      <ListItem 
                        key={milestone.id} 
                        sx={{ 
                          p: 3, 
                          borderBottom: index < companyHistory.milestones.length - 1 ? '1px solid var(--light-gray)' : 0,
                          '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.02)',
                            borderRadius: 2
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Chip 
                            label={milestone.year} 
                            sx={{ 
                              fontWeight: 700,
                              backgroundColor: 'var(--navy-blue)',
                              color: 'white',
                              fontSize: '1rem',
                              padding: '8px 4px'
                            }} 
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={getTranslatedData(milestone.title)} 
                          secondary={getTranslatedData(milestone.description)}
                          primaryTypographyProps={{ 
                            fontWeight: 600, 
                            color: 'var(--navy-blue)', 
                            fontSize: '1.1rem',
                            mb: 1
                          }}
                          secondaryTypographyProps={{
                            lineHeight: 1.6
                          }}
                        />
                      </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          {/* Certificates */}
          {safeSections?.certificates?.isActive && certificates && certificates.length > 0 && (
            <Box sx={{ mb: 8 }}>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700, 
                  textAlign: 'center', 
                  mb: 6, 
                  color: 'var(--navy-blue)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 60,
                    height: 4,
                    backgroundColor: 'var(--light-blue-cta)',
                    borderRadius: 2
                  }
                }}
              >
                {t('about_us.our_certificates')}
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, 
                gap: 4 
              }}>
                {certificates
                  .filter((cert: any) => cert.isActive)
                  .sort((a: any, b: any) => a.order - b.order)
                  .map((cert: any) => (
                    <Card 
                      key={cert.id} 
                      sx={{ 
                        backgroundColor: 'white', 
                        borderRadius: 4, 
                        boxShadow: '0 8px 24px rgba(0,0,0,0.05)', 
                        display: 'flex', 
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 32px rgba(0,0,0,0.15)'
                        }
                      }}
                    >
                      <CardMedia 
                        component="img" 
                        height="200" 
                        image={cert.image} 
                        alt={getTranslatedData(cert.name)} 
                        sx={{ 
                          objectFit: 'cover', 
                          p: 2,
                          borderRadius: '16px 16px 0 0'
                        }} 
                      />
                      <CardContent sx={{ textAlign: 'center', flexGrow: 1, p: 3 }}>
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'var(--navy-blue)',
                            mb: 1,
                            lineHeight: 1.4
                          }}
                        >
                          {getTranslatedData(cert.name)}
                        </Typography>
                        <Typography 
                          color="text.secondary"
                          sx={{ mb: 2, fontWeight: 500 }}
                        >
                          {getTranslatedData(cert.issuer)} - {cert.year}
                        </Typography>
                        {cert.description && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ lineHeight: 1.6 }}
                          >
                            {getTranslatedData(cert.description)}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </Box>
            </Box>
          )}
        </Container>
      </Box>
      <WhatsAppButton />
    </>
  );
};

export default AboutPage; 